import * as Sentry from '@sentry/react';
import { call, fork, put, select } from 'redux-saga/effects';
import { getAll } from 'api/cloudsave/storageReads';
import type { ApiStorageUnitFilter } from 'api/cloudsave/cloudsaveApi';
import { isPendingCreation } from 'containers/Dashboard/utils/isPendingCreation';
import { errorMessage } from 'containers/Snackbar/snackbarMessages';
import { getIsOnline } from 'store/connection/selectors';
import type { RootState } from 'store';
import { withRetry } from 'store/sagaUtils';
import { addMessage } from 'store/snackbar/reducer';
import { storageUnitCache } from 'util/cache/storageUnitCacheIndexedDB';
import { enhanceWithLegacyThumbnails } from 'util/legacyThumbnailHelper';
import { sortStorageUnits } from 'util/storageUnitSort';
import {
  getStorageUnitsResponse,
  setExplorerLoading,
  setMoreDataToRetrieve,
} from './reducer';
import type { GetStorageUnitsRequestActionInterface } from './sagaActions';
import type { AdvancedFilters, Sort, StorageUnit } from './types';
import {
  Filter,
  ROOT,
  SortDirection,
  SortType,
  StorageUnitType,
} from './types';
import {
  mapFilterToApiFilter,
  matchesStorageUnitFilters,
  mergeStorageUnits,
  scopeStorageUnits,
} from './browsingUtils';
import { toIsoString } from './utils';

const canReadDownloadedCache = (
  filters: Filter[] | undefined,
  advancedFilters: AdvancedFilters | undefined,
) =>
  !!filters?.includes(Filter.DOWNLOADED) &&
  !filters.includes(Filter.SHARED) &&
  !filters.includes(Filter.PRIVATE) &&
  !advancedFilters?.country &&
  !advancedFilters?.application;

function* respondWithStorageUnits(params: {
  units: StorageUnit[];
  sort?: Sort;
  replace?: boolean;
  clientFilters?: Filter[];
  advancedFilters?: AdvancedFilters;
  moreDataSource?: StorageUnit[];
  limit?: number;
  offset?: number;
}): Generator<unknown, void, StorageUnit | null> {
  const {
    units,
    sort,
    replace,
    clientFilters,
    advancedFilters,
    moreDataSource,
    limit,
    offset,
  } = params;
  const sortedUnits = sortStorageUnits(
    [...units],
    sort ?? { type: SortType.LAST_MODIFIED, direction: SortDirection.ASC },
  );
  const filteredUnits: StorageUnit[] = yield* filterStorageUnitsForClient(
    sortedUnits,
    clientFilters,
    advancedFilters,
  );

  yield put(
    getStorageUnitsResponse({
      storageUnits: filteredUnits,
      sort,
      replace,
      fetchedCount:
        offset === undefined
          ? undefined
          : offset +
            (moreDataSource ?? units).filter(
              (unit) => unit.type === StorageUnitType.File,
            ).length,
    }),
  );

  const files = (moreDataSource ?? sortedUnits).filter(
    (unit) => unit.type === StorageUnitType.File,
  );
  yield put(setMoreDataToRetrieve(files.length === limit));
}

function* fetchAndEnhanceStorageUnits(params: {
  parentId?: string;
  searchString?: string;
  limit?: number;
  offset?: number;
  filter?: ApiStorageUnitFilter;
  type?: StorageUnitType;
  country?: string;
  application?: string;
}): Generator<unknown, StorageUnit[], Awaited<ReturnType<typeof getAll>>> {
  const response: Awaited<ReturnType<typeof getAll>> = yield withRetry(() =>
    getAll(params),
  );
  let parsedBody = response.parsedBody ?? [];
  if (
    !params.searchString &&
    params.offset === 0 &&
    (params.filter || params.country || params.application)
  ) {
    // Filtered server requests return files only. An unfiltered request with
    // limit 0 returns every folder in the current location.
    const foldersResponse: Awaited<ReturnType<typeof getAll>> = yield withRetry(
      () => getAll({ parentId: params.parentId, limit: 0, offset: 0 }),
    );
    parsedBody = mergeStorageUnits(
      parsedBody,
      (foldersResponse.parsedBody ?? []).filter(
        (unit) => unit.type === StorageUnitType.Folder,
      ),
    );
  }
  const enhanced = enhanceWithLegacyThumbnails(parsedBody ?? []);
  return enhanced.map((unit) => ({
    ...unit,
    isSynced: unit.isSynced ?? true,
    syncedAt: unit.syncedAt ?? toIsoString(unit.updatedAt ?? unit.createdAt),
    localUpdatedAt: null,
    pendingAction: null,
  }));
}

function* getPendingUnits(parentId?: string, type?: StorageUnitType) {
  const pending: StorageUnit[] = yield call(
    [storageUnitCache, storageUnitCache.getAllByParent],
    parentId,
    type,
  );

  return pending.filter(
    (unit) =>
      unit.pendingAction === 'create' ||
      unit.pendingAction === 'update' ||
      unit.isSynced === false,
  );
}

function* mergeWithPendingUnits(
  units: StorageUnit[],
  parentId?: string,
  type?: StorageUnitType,
) {
  const pendingUnits: StorageUnit[] = yield* getPendingUnits(parentId, type);
  return mergeStorageUnits(units, pendingUnits);
}

function* getOfflineChildren({
  parentId,
  offset,
  sort,
  replace,
  type,
  cacheKey,
  clientFilters,
  advancedFilters,
}: {
  parentId?: string;
  offset?: number;
  sort?: Sort;
  replace?: boolean;
  type?: StorageUnitType;
  cacheKey: string;
  clientFilters?: Filter[];
  advancedFilters?: AdvancedFilters;
}) {
  if (offset && offset > 0) {
    yield put(setMoreDataToRetrieve(false));
    return;
  }

  let offlineUnits: StorageUnit[];

  if (canReadDownloadedCache(clientFilters, advancedFilters)) {
    offlineUnits = yield call(
      [storageUnitCache, storageUnitCache.getDownloadedByParent],
      parentId,
    );
  } else {
    const cachedList: StorageUnit[] | null = yield call(
      [storageUnitCache, storageUnitCache.getList],
      cacheKey,
    );
    const cachedUnits: StorageUnit[] =
      (cachedList ? scopeStorageUnits(cachedList, parentId, type) : null) ??
      (yield call(
        [storageUnitCache, storageUnitCache.getAllByParent],
        parentId,
        type,
      ));

    offlineUnits = yield* mergeWithPendingUnits(cachedUnits, parentId, type);
  }

  yield* respondWithStorageUnits({
    units: offlineUnits,
    sort,
    replace,
    clientFilters: canReadDownloadedCache(clientFilters, advancedFilters)
      ? clientFilters?.filter((filter) => filter !== Filter.DOWNLOADED)
      : clientFilters,
    advancedFilters,
    offset,
  });
  yield put(setMoreDataToRetrieve(false));
}

function* fetchChildrenFromBackend(
  parentId: string | undefined,
  limit: number | undefined,
  offset: number | undefined,
  sort: Sort | undefined,
  replace: boolean | undefined,
  filter: ApiStorageUnitFilter | undefined,
  type: StorageUnitType | undefined,
  cacheKey: string,
  advancedFilters?: AdvancedFilters,
  clientFilters?: Filter[],
) {
  const isOnline: boolean = yield select(getIsOnline);
  if (!isOnline) {
    return;
  }

  try {
    const enhancedBody = yield* fetchAndEnhanceStorageUnits({
      parentId,
      limit,
      offset,
      filter,
      type,
      country: advancedFilters?.country,
      application: advancedFilters?.application,
    });

    const mergedBody: StorageUnit[] = yield* mergeWithPendingUnits(
      enhancedBody,
      parentId,
      type,
    );

    yield storageUnitCache.setList(mergedBody, cacheKey);

    yield* respondWithStorageUnits({
      units: mergedBody,
      sort,
      replace,
      clientFilters,
      offset,
      advancedFilters,
      moreDataSource: enhancedBody,
      limit,
    });
  } catch (fetchError) {
    Sentry.captureException(fetchError, {
      tags: { saga: 'fetchChildrenFromBackend' },
      extra: { parentId, limit, offset, filter, type },
    });
  }
}

function* getChildren(
  parentId?: string,
  limit?: number,
  offset?: number,
  sort?: Sort,
  replace?: boolean,
  filter?: ApiStorageUnitFilter,
  type?: StorageUnitType,
  advancedFilters?: AdvancedFilters,
  clientFilters?: Filter[],
) {
  try {
    if (offset === 0) {
      yield put(setExplorerLoading(true));
    }

    const advancedFilterKey =
      // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
      advancedFilters?.country || advancedFilters?.application
        ? `&country=${advancedFilters.country ?? ''}&application=${
            advancedFilters.application ?? ''
          }`
        : '';
    const cacheKey = `parentId=${parentId}&limit=${limit}&offset=${offset}&filter=${filter}&type=${type}${advancedFilterKey}`;

    const isOnline: boolean = yield select(getIsOnline);
    if (!isOnline || (parentId && isPendingCreation(parentId))) {
      yield* getOfflineChildren({
        parentId,
        offset,
        sort,
        replace,
        type,
        cacheKey,
        clientFilters,
        advancedFilters,
      });
      return;
    }

    if (canReadDownloadedCache(clientFilters, advancedFilters)) {
      if (offset && offset > 0) {
        yield put(setMoreDataToRetrieve(false));
        return;
      }

      const downloaded: StorageUnit[] = yield call(
        [storageUnitCache, storageUnitCache.getDownloadedByParent],
        parentId,
      );
      const mergedDownloaded: StorageUnit[] = yield* mergeWithPendingUnits(
        downloaded,
        parentId,
        type,
      );
      yield* respondWithStorageUnits({
        units: mergedDownloaded,
        sort,
        replace,
        clientFilters: clientFilters?.filter(
          (filter) => filter !== Filter.DOWNLOADED,
        ),
        offset,
      });
      yield put(setMoreDataToRetrieve(false));
      return;
    }

    const cachedList: Awaited<ReturnType<typeof storageUnitCache.getList>> =
      yield storageUnitCache.getList(cacheKey);

    let hadCache = false;
    if (cachedList) {
      const scopedCachedList = scopeStorageUnits(cachedList, parentId, type);
      const mergedCached: StorageUnit[] = yield* mergeWithPendingUnits(
        scopedCachedList,
        parentId,
        type,
      );

      yield* respondWithStorageUnits({
        units: mergedCached,
        sort,
        replace,
        clientFilters,
        advancedFilters,
        limit,
        offset,
      });
      hadCache = true;
    }

    if (hadCache) {
      yield fork(
        fetchChildrenFromBackend,
        parentId,
        limit,
        offset,
        sort,
        replace,
        filter,
        type,
        cacheKey,
        advancedFilters,
        clientFilters,
      );
    } else {
      const enhancedBody = yield* fetchAndEnhanceStorageUnits({
        parentId,
        limit,
        offset,
        filter,
        type,
        country: advancedFilters?.country,
        application: advancedFilters?.application,
      });

      const mergedBody: StorageUnit[] = yield* mergeWithPendingUnits(
        enhancedBody,
        parentId,
        type,
      );

      yield storageUnitCache.setList(mergedBody, cacheKey);

      yield* respondWithStorageUnits({
        units: mergedBody,
        sort,
        replace,
        clientFilters,
        advancedFilters,
        moreDataSource: enhancedBody,
        limit,
        offset,
      });
    }
  } catch (err) {
    const offlineUnits: StorageUnit[] = yield call(
      [storageUnitCache, storageUnitCache.getAllByParent],
      parentId,
      type,
    );

    if (offlineUnits.length) {
      const sortedOffline = sortStorageUnits(
        [...offlineUnits],
        sort ?? { type: SortType.LAST_MODIFIED, direction: SortDirection.ASC },
      );
      const filteredOffline: StorageUnit[] = yield* filterStorageUnitsForClient(
        sortedOffline,
        clientFilters,
        advancedFilters,
      );

      const existing: StorageUnit[] = yield select(
        (state: RootState) => state.storageUnitsState.storageUnits,
      );
      const offlineIds = new Set(filteredOffline.map((unit) => unit.id));
      const merged = sortStorageUnits(
        [
          ...filteredOffline,
          ...existing.filter((unit) => !offlineIds.has(unit.id)),
        ],
        sort ?? { type: SortType.LAST_MODIFIED, direction: SortDirection.ASC },
      );

      yield put(
        getStorageUnitsResponse({
          storageUnits: merged,
          sort,
          replace,
        }),
      );
      yield put(setMoreDataToRetrieve(false));
    } else {
      Sentry.captureException(err, {
        tags: { saga: 'getChildren' },
        extra: { parentId, limit, offset, filter, type },
      });
      yield put(
        addMessage(
          errorMessage(
            'Unable to fetch files and folders. Please try again later.',
          ),
        ),
      );
    }
  } finally {
    yield put(setExplorerLoading(false));
  }
}

function* getSearchResult(
  parentId?: string,
  searchString?: string,
  limit?: number,
  offset?: number,
  sort?: Sort,
  filters: Filter[] = [],
  advancedFilters?: AdvancedFilters,
) {
  try {
    // Initial data retrieval
    if (offset === 0) {
      yield put(setExplorerLoading(true));
    }

    const isOnline: boolean = yield select(getIsOnline);
    if (!isOnline) {
      if (offset && offset > 0) {
        yield put(setMoreDataToRetrieve(false));
        return;
      }

      const cachedUnits: StorageUnit[] = yield call(
        [storageUnitCache, storageUnitCache.getAllByParent],
        parentId,
      );
      const normalizedSearch = searchString?.trim().toLowerCase() ?? '';
      const searchedUnits = normalizedSearch
        ? cachedUnits.filter((unit) =>
            unit.name.toLowerCase().includes(normalizedSearch),
          )
        : cachedUnits;
      const sortedOffline = sortStorageUnits(
        searchedUnits,
        sort ?? { type: SortType.LAST_MODIFIED, direction: SortDirection.ASC },
      );
      const filteredOffline: StorageUnit[] = yield* filterStorageUnitsForClient(
        sortedOffline,
        filters,
        advancedFilters,
      );

      yield put(
        getStorageUnitsResponse({
          storageUnits: filteredOffline,
          sort,
          fetchedCount: searchedUnits.length,
        }),
      );
      yield put(setMoreDataToRetrieve(false));
      return;
    }

    const enhancedBody = yield* fetchAndEnhanceStorageUnits({
      parentId,
      searchString,
      limit,
      offset,
      filter: mapFilterToApiFilter(
        filters.includes(Filter.SHARED)
          ? Filter.SHARED
          : filters.find((f) => mapFilterToApiFilter(f)),
      ),
      country: advancedFilters?.country,
      application: advancedFilters?.application,
    });

    const sortedBody = sortStorageUnits(
      enhancedBody,
      sort ?? { type: SortType.LAST_MODIFIED, direction: SortDirection.ASC },
    );
    const filteredBody: StorageUnit[] = yield* filterStorageUnitsForClient(
      sortedBody,
      filters,
      advancedFilters,
    );
    yield put(
      getStorageUnitsResponse({
        storageUnits: filteredBody,
        sort,
        replace: offset === 0,
        fetchedCount: (offset ?? 0) + enhancedBody.length,
      }),
    );
    yield put(setMoreDataToRetrieve(enhancedBody.length === limit));
  } catch (err) {
    Sentry.captureException(err, {
      tags: { saga: 'getSearchResult' },
      extra: { parentId, searchString, limit, offset, filters },
    });
    yield put(
      addMessage(
        errorMessage("Couldn't fetch search result. Please try again later."),
      ),
    );
  } finally {
    yield put(setExplorerLoading(false));
  }
}

function* getCachedAncestorPath(
  descendantId: string,
): Generator<unknown, StorageUnit[], unknown> {
  const knownUnits = (yield select(
    (state: RootState) => state.storageUnitsState.storageUnits,
  )) as StorageUnit[];
  const knownById = new Map(knownUnits.map((unit) => [unit.id, unit]));
  const folderPath: StorageUnit[] = [];
  const visited = new Set<string>();
  let currentId: string | undefined = descendantId;

  while (currentId && currentId !== ROOT.id && !visited.has(currentId)) {
    visited.add(currentId);

    let currentUnit = (yield call(
      [storageUnitCache, storageUnitCache.getOne],
      currentId,
    )) as StorageUnit | null;
    currentUnit = currentUnit ?? knownById.get(currentId) ?? null;

    if (currentUnit?.type !== StorageUnitType.Folder) {
      break;
    }

    folderPath.unshift(currentUnit);
    currentId = currentUnit.parentId;
  }

  return folderPath;
}

function* getAncestors(descendantId: string, _replace?: boolean) {
  try {
    const isOnline: boolean = yield select(getIsOnline);
    if (!isOnline || isPendingCreation(descendantId)) {
      const folderPath: StorageUnit[] =
        yield* getCachedAncestorPath(descendantId);
      yield put(getStorageUnitsResponse({ folderPath }));
      return;
    }

    const { parsedBody } = yield withRetry(() => getAll({ descendantId }));
    yield put(getStorageUnitsResponse({ folderPath: parsedBody }));
  } catch (err) {
    Sentry.captureException(err, {
      tags: { saga: 'getAncestors' },
      extra: { descendantId },
    });
  }
}

function* filterStorageUnitsForClient(
  units: StorageUnit[],
  filters: Filter[] = [],
  advancedFilters?: AdvancedFilters,
) {
  if (
    !filters.length &&
    !advancedFilters?.country &&
    !advancedFilters?.application
  ) {
    return units;
  }

  let filteredUnits = units.filter((unit) =>
    matchesStorageUnitFilters(unit, filters, advancedFilters),
  );
  if (filters.includes(Filter.DOWNLOADED) || filters.includes(Filter.CLOUD)) {
    const localSelected = filters.includes(Filter.DOWNLOADED);
    const filtered: StorageUnit[] = [];
    for (const unit of filteredUnits) {
      if (unit.type !== StorageUnitType.File) {
        filtered.push(unit);
        continue;
      }
      const cached: StorageUnit | null = yield call(
        [storageUnitCache, storageUnitCache.getOne],
        unit.id,
      );
      if (Boolean(cached?.fileData) === localSelected) {
        filtered.push(unit);
      }
    }
    filteredUnits = filtered;
  }
  return filteredUnits;
}

export function* getStorageUnits(
  action: GetStorageUnitsRequestActionInterface,
) {
  const { parentId, descendantId, searchString, limit, offset, replace, type } =
    action.payload;

  const filters: Filter[] = yield select(
    (s: RootState) => s.storageUnitsState.filters,
  );

  const advancedFilters: AdvancedFilters = yield select(
    (s: RootState) => s.storageUnitsState.advancedFilters,
  );

  const sort: Sort = yield select((s: RootState) => s.storageUnitsState.sort);
  if (descendantId) {
    yield getAncestors(descendantId, replace);
  } else if (searchString) {
    yield getSearchResult(
      parentId,
      searchString,
      limit,
      offset,
      sort,
      filters,
      advancedFilters,
    );
  } else {
    const requestFilter = mapFilterToApiFilter(
      filters.includes(Filter.SHARED)
        ? Filter.SHARED
        : filters.find((f) => mapFilterToApiFilter(f)),
    );

    yield getChildren(
      parentId,
      limit,
      offset,
      sort,
      replace,
      requestFilter,
      type,
      advancedFilters,
      filters,
    );
  }
}
